import { useState } from 'react';

interface SliderCategory {
  name: string;
  icon: string;
  min: number;
  max: number;
  defaultValue: number;
  impact: number; // Weight percentage
  description: string;
}

export default function SampleScoreSlider() {
  const categories: SliderCategory[] = [
    {
      name: 'Cardiovascular',
      icon: 'ri-heart-pulse-line',
      min: 0,
      max: 100,
      defaultValue: 75,
      impact: 25,
      description: 'Heart health & fitness'
    },
    {
      name: 'Metabolic',
      icon: 'ri-pulse-line',
      min: 0,
      max: 100,
      defaultValue: 70,
      impact: 25,
      description: 'Weight & nutrition'
    },
    {
      name: 'Lifestyle',
      icon: 'ri-run-line',
      min: 0,
      max: 100,
      defaultValue: 65,
      impact: 25,
      description: 'Daily habits'
    },
    {
      name: 'Recovery',
      icon: 'ri-moon-line',
      min: 0,
      max: 100,
      defaultValue: 80,
      impact: 15,
      description: 'Sleep & rest'
    },
    {
      name: 'Mental',
      icon: 'ri-mental-health-line',
      min: 0,
      max: 100,
      defaultValue: 72,
      impact: 10,
      description: 'Stress & wellbeing'
    }
  ];

  const [values, setValues] = useState<Record<string, number>>(
    categories.reduce((acc, cat) => ({ ...acc, [cat.name]: cat.defaultValue }), {})
  );

  const calculateTotalScore = () => {
    let total = 0;
    categories.forEach(cat => {
      total += (values[cat.name] || cat.defaultValue) * (cat.impact / 100);
    });
    return Math.round(total * 10);
  };

  const handleSliderChange = (name: string, value: number) => {
    setValues(prev => ({ ...prev, [name]: value }));
  };

  const totalScore = calculateTotalScore();

  const getScoreColor = () => {
    if (totalScore >= 900) return 'text-green-600';
    if (totalScore >= 800) return 'text-teal-600';
    if (totalScore >= 700) return 'text-blue-600';
    if (totalScore >= 600) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreLabel = () => {
    if (totalScore >= 900) return 'Excellent';
    if (totalScore >= 800) return 'Very Good';
    if (totalScore >= 700) return 'Good';
    if (totalScore >= 600) return 'Fair';
    return 'Needs Attention';
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-gray-200 p-8 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Interactive Score Calculator
          </h3>
          <p className="text-gray-600">
            Adjust the sliders to see how different categories affect your total score
          </p>
        </div>
        <div className="text-center">
          <div className={`text-5xl font-bold ${getScoreColor()} transition-all duration-300`}>
            {totalScore}
          </div>
          <div className="text-sm text-gray-600 mt-1">out of 1000</div>
          <div className={`mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
            totalScore >= 900 ? 'bg-green-100 text-green-700' :
            totalScore >= 800 ? 'bg-teal-100 text-teal-700' :
            totalScore >= 700 ? 'bg-blue-100 text-blue-700' :
            totalScore >= 600 ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>
            {getScoreLabel()}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {categories.map((category, idx) => {
          const currentValue = values[category.name] || category.defaultValue;
          const contribution = Math.round(currentValue * (category.impact / 100) * 10);
          
          return (
            <div 
              key={category.name}
              className="animate-slideInRight"
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className={`${category.icon} text-xl text-teal-600`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{category.name}</div>
                    <div className="text-xs text-gray-500">{category.description}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-900">{currentValue}</div>
                  <div className="text-xs text-teal-600 font-semibold">
                    +{contribution} pts ({category.impact}%)
                  </div>
                </div>
              </div>

              <div className="relative">
                <input
                  type="range"
                  min={category.min}
                  max={category.max}
                  value={currentValue}
                  onChange={(e) => handleSliderChange(category.name, parseInt(e.target.value))}
                  className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #14b8a6 0%, #14b8a6 ${currentValue}%, #e5e7eb ${currentValue}%, #e5e7eb 100%)`
                  }}
                />
              </div>

              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Poor</span>
                <span>Average</span>
                <span>Excellent</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 pt-6 border-t-2 border-gray-200">
        <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
              <i className="ri-information-line text-teal-600"></i>
            </div>
            <div className="text-sm text-gray-700">
              <strong>How it works:</strong> Your total score is calculated by weighting each category. 
              Categories with higher impact percentages contribute more to your final score. 
              Try adjusting the sliders to see how improvements in different areas affect your overall health score!
            </div>
          </div>
        </div>
      </div>

      <style>{`
        input[type='range']::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #14b8a6;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }

        input[type='range']::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.4);
        }

        input[type='range']::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #14b8a6;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transition: all 0.2s ease;
        }

        input[type='range']::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.4);
        }
      `}</style>
    </div>
  );
}
